import { ChartCanvas, Chart } from "@react-financial-charts/core";
import { XAxis, YAxis } from "@react-financial-charts/axes";
import { LineSeries } from "@react-financial-charts/series";
import { CrossHairCursor, MouseCoordinateX, MouseCoordinateY } from "@react-financial-charts/coordinates";
import { format } from "d3-format";
import type { FlowsPointDto } from "../api";
import {
  CHART_HEIGHT, FII_COLOR, DII_COLOR, VIX_UP_COLOR,
  useContainerWidth, buildScale, symmetricExtent, NotEnoughPoints,
} from "./flowsChartShared";

/** TAB 2 "Relative moves" -- FII net, DII net, and VIX day-change all
 * standardized to z-scores (std devs from THIS WINDOW's own mean) so all
 * three sit on one common, unitless, 0-centered scale -- lets a viewer
 * compare MAGNITUDES directly ("VIX +2σ, FII -1.5σ") the way raw ₹cr vs VIX
 * points never could. Computed server-side (app/api/routes/market.py's
 * _flows_series) from only the real values present in this window -- a gap
 * day contributes to neither the mean nor the std dev, matching every other
 * tab's gap discipline. A flat/undefined series here (all null z-scores)
 * means the window had too few real points or zero variance -- an honest
 * "nothing to standardize," not a chart bug.
 */

interface ChartDatum {
  date: Date;
  fii_z: number | null;
  dii_z: number | null;
  vix_change_z: number | null;
}

export function FlowsRelativeChart({ points }: { points: FlowsPointDto[] }) {
  const { ref, width } = useContainerWidth<HTMLDivElement>();

  const data: ChartDatum[] = points.map((p) => ({
    date: new Date(p.date), fii_z: p.fii_z, dii_z: p.dii_z, vix_change_z: p.vix_change_z,
  }));

  if (data.length < 2 || width === 0) {
    return data.length < 2 ? <NotEnoughPoints containerRef={ref} /> : <div ref={ref} style={{ minHeight: 40 }} />;
  }

  const { plotData, xScale, xAccessor, displayXAccessor, xExtents, formatTickDate } = buildScale(data);
  const allZ = data.flatMap((d) => [d.fii_z, d.dii_z, d.vix_change_z]);
  const anyZ = allZ.some((v) => v != null);
  const yExtents = symmetricExtent(allZ, 0.15, 0.5);

  return (
    <div ref={ref}>
      <div className="flows-legend">
        <span><i style={{ background: FII_COLOR }} /> FII net (z-score)</span>
        <span><i style={{ background: DII_COLOR }} /> DII net (z-score)</span>
        <span><i style={{ background: VIX_UP_COLOR }} /> VIX day-change (z-score)</span>
      </div>
      {!anyZ ? (
        <div className="empty-state" style={{ padding: 8 }}>
          Not enough real observations in this window to compute a z-score (need at least 2, with non-zero variance).
        </div>
      ) : (
        <ChartCanvas
          height={CHART_HEIGHT}
          width={width}
          ratio={window.devicePixelRatio || 1}
          margin={{ left: 56, right: 8, top: 8, bottom: 24 }}
          data={plotData}
          seriesName="flows-relative"
          xScale={xScale}
          xAccessor={xAccessor}
          displayXAccessor={displayXAccessor}
          xExtents={xExtents}
        >
          <Chart id={0} height={CHART_HEIGHT} yExtents={yExtents} padding={{ top: 10, bottom: 10 }}>
            <XAxis showGridLines tickFormat={formatTickDate("%d %b")} />
            <YAxis showGridLines tickFormat={format("+.1f")} />
            <LineSeries yAccessor={(d: ChartDatum) => d.fii_z ?? undefined} strokeStyle={FII_COLOR} strokeWidth={1.4} />
            <LineSeries yAccessor={(d: ChartDatum) => d.dii_z ?? undefined} strokeStyle={DII_COLOR} strokeWidth={1.4} />
            <LineSeries yAccessor={(d: ChartDatum) => d.vix_change_z ?? undefined} strokeStyle={VIX_UP_COLOR} strokeWidth={1.4} strokeDasharray="Dash" />
            <MouseCoordinateY displayFormat={format("+.2f")} />
            <MouseCoordinateX displayFormat={formatTickDate("%d %b %Y")} />
          </Chart>
          <CrossHairCursor />
        </ChartCanvas>
      )}
    </div>
  );
}
